import { HttpStatusCode } from '@angular/common/http';
import { Action } from '@ngrx/store';
import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, mergeMap, take } from 'rxjs/operators';
import {
  CreateCustomerRequest,
  Customer,
  UpdateCustomerRequest,
} from '../client/src';

import { ICustomerDetailsPage, LoggingService } from '@nx-ng/types';
import { CustomerStoreActions } from '../actions';
import { CustomerStoreFacade } from '../facade';
import {
  formatCustomerPageForCreate,
  formatCustomerPageForUpdate,
} from '../util/format-customer-for-api';
import { B2CEventsService } from '../../../app/b2c/b2c-events.service';
import { CustomerService } from '../services';
import { MsalPolicyService } from '@modules/core/services/msalPolicy.service';
import { ApiError } from '@modules/checkout-store';
import { isApiErrorBody } from '@nx-ng/util';
import { LOGGER } from '@modules/core';

@Injectable()
export class CustomerStoreEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly customerFacade: CustomerStoreFacade,
    private readonly b2cEventsService: B2CEventsService,
    private customerService: CustomerService,
    private msalPolicyService: MsalPolicyService,
    @Inject(LOGGER) private logger: LoggingService
  ) {}

  triggerLoadCustomerOnB2CLogin$ = createEffect(() =>
    this.b2cEventsService.isLoggedIn$.pipe(
      switchMap((isLoggedIn: boolean | undefined) => {
        if (isLoggedIn) {
          return [CustomerStoreActions.loadDataAccessCustomerDetails()];
        }
        return [];
      })
    )
  );

  loadCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerStoreActions.loadDataAccessCustomerDetails),
      switchMap(() => this.msalPolicyService.token$),
      switchMap((token: string | undefined) => {
        if (token === undefined) {
          return EMPTY;
        }
        return this.customerService.getCustomer(token).pipe(
          switchMap(
            (
              value: Customer | { body: ApiError } | undefined,
              _index: number
            ) => {
              if (
                isApiErrorBody(value) &&
                value.body.code == HttpStatusCode.NotFound
              ) {
                return [
                  CustomerStoreActions.loadDataAccessCustomerDetailsNotFound(),
                ];
              }
              if (isApiErrorBody(value)) {
                return [
                  CustomerStoreActions.loadDataAccessCustomerDetailsFailure({
                    error: value.body,
                    retryAction:
                      CustomerStoreActions.loadDataAccessCustomerDetails(),
                  }),
                ];
              }
              if (value) {
                return [
                  CustomerStoreActions.loadDataAccessCustomerDetailsSuccess({
                    loadedCustomer: value,
                  }),
                  CustomerStoreActions.customerAPI_loadBillingCustomerApplicants(
                    {
                      billingCustomerEmail: value.emailAddress,
                    }
                  ),
                ];
              }
              return [
                CustomerStoreActions.loadDataAccessCustomerDetailsFailure({
                  error: 'empty response',
                  retryAction:
                    CustomerStoreActions.loadDataAccessCustomerDetails(),
                }),
              ];
            }
          ),
          catchError((err) => {
            const apiError: ApiError | string =
              err?.response?.parsedBody || 'Load Customer API call has failed';

            this.logger.logApiError(
              {
                apiError,
              },
              err
            );
            return of(
              CustomerStoreActions.loadDataAccessCustomerDetailsFailure({
                error: apiError,
                retryAction:
                  CustomerStoreActions.loadDataAccessCustomerDetails(),
              })
            );
          })
        );
      })
    )
  );

  saveCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerStoreActions.saveCustomerFromAccountPage),
      switchMap((action) => {
        return combineLatest([
          of(action.customerFormData),
          this.customerFacade.existingCustomer$,
          this.b2cEventsService.userName$,
        ]).pipe(take(1));
      }),
      switchMap(
        ([customerFormData, existingCustomer, email]: [
          ICustomerDetailsPage,
          Customer | undefined,
          string
        ]) => {
          if (!existingCustomer?.id) {
            const apiCustomer = formatCustomerPageForCreate({
              email,
              customer: customerFormData,
            });
            return of(
              CustomerStoreActions.customerAPI_CreateCustomer({
                customerToCreate: apiCustomer,
              })
            );
          } else {
            const apiCustomer = formatCustomerPageForUpdate({
              existingCustomer,
              updateCustomerDetails: customerFormData,
            });
            return of(
              CustomerStoreActions.customerAPI_UpdateCustomer({
                customerId: existingCustomer.id,
                customerToUpdate: apiCustomer,
              })
            );
          }
        }
      )
    )
  );

  createCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerStoreActions.customerAPI_CreateCustomer),
      switchMap((action) => {
        return combineLatest([of(action), this.msalPolicyService.token$]).pipe(
          take(1)
        );
      }),
      switchMap(
        ([action, token]: [
          { customerToCreate: CreateCustomerRequest } & Action,
          string | undefined
        ]) => {
          const { customerToCreate } = action;
          return this.customerService
            .createCustomer(customerToCreate, token)
            .pipe(
              map((value, _index: number) => {
                if (value) {
                  return CustomerStoreActions.customerAPI_CreateCustomerSuccess(
                    {
                      createdCustomer: value,
                    }
                  );
                }
                return CustomerStoreActions.customerAPI_CreateCustomerFailure({
                  error: 'empty response',
                  retryAction: action,
                });
              }),
              catchError((err) => {
                const apiError: ApiError | string =
                  err?.response?.parsedBody ||
                  'Create Customer API call has failed';

                this.logger.logApiError(
                  {
                    apiError,
                  },
                  err
                );
                return of(
                  CustomerStoreActions.customerAPI_CreateCustomerFailure({
                    error: apiError,
                    retryAction: action,
                  })
                );
              })
            );
        }
      )
    )
  );

  updateCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerStoreActions.customerAPI_UpdateCustomer),
      switchMap((action) => {
        return combineLatest([of(action), this.msalPolicyService.token$]).pipe(
          take(1)
        );
      }),
      switchMap(
        ([action, token]: [
          {
            customerId: string;
            customerToUpdate: UpdateCustomerRequest;
          } & Action,
          string | undefined
        ]) => {
          const { customerId, customerToUpdate } = action;
          return this.customerService
            .updateCustomer(customerId, customerToUpdate, token)
            .pipe(
              map((value, _index: number) => {
                if (value) {
                  return CustomerStoreActions.customerAPI_UpdateCustomerSuccess(
                    {
                      updatedCustomer: value,
                    }
                  );
                }
                return CustomerStoreActions.customerAPI_UpdateCustomerFailure({
                  error: 'empty response',
                  retryAction: action,
                });
              }),
              catchError((err) => {
                const apiError: ApiError | string =
                  err?.response?.parsedBody ||
                  'Update Customer API call has failed';

                this.logger.logApiError(
                  {
                    apiError,
                  },
                  err
                );

                return of(
                  CustomerStoreActions.customerAPI_UpdateCustomerFailure({
                    error: apiError,
                    retryAction: action,
                  })
                );
              })
            );
        }
      )
    )
  );

  getSwiftCardsForApplicant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerStoreActions.customerAPI_GetApplicantSwiftCards),
      mergeMap((action) =>
        combineLatest([this.customerFacade.customerEmail$, of(action)]).pipe(
          take(1)
        )
      ),
      mergeMap(
        ([customerEmail, action]: [
          string,
          { applicantCustomerID: string; ticketID: number } & Action
        ]) => {
          const { applicantCustomerID, ticketID } = action;
          return this.customerService
            .getSwiftCardsForApplicant(
              customerEmail,
              applicantCustomerID,
              ticketID
            )
            .pipe(
              map((value, _index: number) => {
                if (
                  isApiErrorBody(value) &&
                  value.body.code == HttpStatusCode.NotFound
                ) {
                  return CustomerStoreActions.customerAPI_GetApplicantSwiftCardsSuccess(
                    {
                      applicantCustomerID,
                      swiftCards: [],
                      ticketID,
                    }
                  );
                }

                if (value) {
                  return CustomerStoreActions.customerAPI_GetApplicantSwiftCardsSuccess(
                    { swiftCards: value, applicantCustomerID, ticketID }
                  );
                }
                return CustomerStoreActions.customerAPI_GetApplicantSwiftCardsFailure(
                  {
                    applicantCustomerID,
                    ticketID,
                    error: 'empty response',
                    retryAction: action,
                  }
                );
              }),
              catchError((error) => {
                const apiError: ApiError | string =
                  error?.response?.parsedBody || error;

                this.logger.logApiError(
                  {
                    apiError,
                  },
                  error
                );
                return of(
                  CustomerStoreActions.customerAPI_GetApplicantSwiftCardsFailure(
                    {
                      error: apiError,
                      retryAction: action,
                      applicantCustomerID,
                      ticketID,
                    }
                  )
                );
              })
            );
        }
      )
    )
  );

  loadApplicantsForBillingCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerStoreActions.customerAPI_loadBillingCustomerApplicants),
      switchMap((action: { billingCustomerEmail: string } & Action) => {
        const { billingCustomerEmail } = action;
        return this.customerService
          .getApplicantsForBillingCustomer(billingCustomerEmail)
          .pipe(
            map((value, _index: number) => {
              if (
                isApiErrorBody(value) &&
                value.body.code == HttpStatusCode.NotFound
              ) {
                return CustomerStoreActions.customerAPI_loadBillingCustomerApplicantsSuccess(
                  {
                    applicants: [],
                  }
                );
              }

              if (value) {
                return CustomerStoreActions.customerAPI_loadBillingCustomerApplicantsSuccess(
                  { applicants: value }
                );
              }
              return CustomerStoreActions.customerAPI_loadBillingCustomerApplicantsFailure(
                { error: 'empty response', retryAction: action }
              );
            }),
            catchError((err) => {
              const apiError: ApiError | string =
                err?.response?.parsedBody ||
                'Load Applicants API call has failed';

              this.logger.logApiError(
                {
                  apiError,
                },
                err
              );
              return of(
                CustomerStoreActions.customerAPI_loadBillingCustomerApplicantsFailure(
                  { error: apiError, retryAction: action }
                )
              );
            })
          );
      })
    )
  );
}
